/** Minimal iCalendar (.ics) generation for confirmed coverage bookings (all-day events). */

export interface IcsEvent {
  readonly uid: string;
  /** Inclusive YYYY-MM-DD dates. */
  readonly start: string;
  readonly end: string;
  readonly summary: string;
  readonly location?: string;
  readonly description?: string;
}

const escape = (v: string): string =>
  v.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

const compact = (iso: string): string => iso.replace(/-/g, '');

/** DTEND for an all-day VEVENT is exclusive, so add one day to the inclusive end. */
function endExclusive(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10).replace(/-/g, '');
}

export function buildIcs(events: readonly IcsEvent[]): string {
  const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Badeel//Coverage//EN',
    'CALSCALE:GREGORIAN',
  ];
  for (const e of events) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:${e.uid}@badeel`,
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${compact(e.start)}`,
      `DTEND;VALUE=DATE:${endExclusive(e.end)}`,
      `SUMMARY:${escape(e.summary)}`,
    );
    if (e.location) lines.push(`LOCATION:${escape(e.location)}`);
    if (e.description) lines.push(`DESCRIPTION:${escape(e.description)}`);
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

/** Triggers a browser download of an .ics file. */
export function downloadIcs(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.ics') ? filename : `${filename}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}
